import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import {
  listAgentConnections,
  listCatalog,
  listConnections,
  setAgentConnections,
  type ConnectorDefinition,
  type Connection,
} from '../connectors/api';

type Props = {
  orgId: string;
  agentId: string;
};

export function AgentConnectionsCard({ orgId, agentId }: Props) {
  const qc = useQueryClient();
  const catalog = useQuery({
    queryKey: ['connector-catalog', orgId],
    queryFn: () => listCatalog(orgId),
    enabled: !!orgId,
  });
  const orgConnections = useQuery({
    queryKey: ['connections', orgId],
    queryFn: () => listConnections(orgId),
    enabled: !!orgId,
  });
  const agentConnections = useQuery({
    queryKey: ['agent-connections', orgId, agentId],
    queryFn: () => listAgentConnections(orgId, agentId),
    enabled: !!orgId && !!agentId,
  });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState(false);

  // Initialise from the server response the first time it arrives, and
  // again if the agent's connections change underneath us (e.g. an
  // admin disconnected one).
  useEffect(() => {
    if (!agentConnections.data) return;
    if (dirty) return;
    setSelected(new Set(agentConnections.data.map((c) => c.id)));
  }, [agentConnections.data, dirty]);

  const save = useMutation({
    mutationFn: () => setAgentConnections(orgId, agentId, Array.from(selected)),
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['agent-connections', orgId, agentId] });
    },
  });

  const byDefinition = useMemo(
    () => groupByDefinition(orgConnections.data ?? []),
    [orgConnections.data],
  );

  const toggle = (id: string) => {
    setDirty(true);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const noOrgConnections = (orgConnections.data ?? []).length === 0;

  return (
    <Card>
      <CardHeader
        title="Connectors"
        description="External data sources and actions this agent can call. Only connections enabled here are exposed to the LLM as tools."
      />
      <CardBody className="space-y-4">
        {noOrgConnections ? (
          <EmptyState />
        ) : (
          <>
            <div className="space-y-3">
              {(catalog.data ?? []).map((def) => {
                const conns = byDefinition[def.slug] ?? [];
                if (conns.length === 0) return null;
                return (
                  <DefinitionGroup
                    key={def.slug}
                    def={def}
                    conns={conns}
                    selected={selected}
                    onToggle={toggle}
                  />
                );
              })}
            </div>

            <div className="flex items-center justify-between border-t border-line-soft pt-3">
              <p className="text-xs text-ink-tertiary">
                {selected.size} connection{selected.size === 1 ? '' : 's'} attached
              </p>
              <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function EmptyState() {
  return (
    <p className="text-sm text-ink-tertiary">
      No connections in this organisation yet.{' '}
      <Link to="/connectors" className="text-bit2me hover:underline">
        Set up a connector ↗
      </Link>
    </p>
  );
}

function DefinitionGroup({
  def,
  conns,
  selected,
  onToggle,
}: {
  def: ConnectorDefinition;
  conns: Connection[];
  selected: Set<string>;
  onToggle: (id: string) => void;
}) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2">
        <p className="text-sm font-semibold text-ink-primary">{def.name}</p>
        <span className="font-mono text-[10px] uppercase text-ink-tertiary">{def.vendor}</span>
      </div>
      <ul className="space-y-1.5">
        {conns.map((c) => (
          <li
            key={c.id}
            className="flex items-center gap-3 rounded border border-line-soft bg-surface-white px-3 py-1.5"
          >
            <input
              type="checkbox"
              checked={selected.has(c.id)}
              onChange={() => onToggle(c.id)}
              className="accent-bit2me"
            />
            <p className="flex-1 text-sm text-ink-primary">{c.account_label}</p>
            <span className="font-mono text-[10px] uppercase text-ink-tertiary">{c.status}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function groupByDefinition(items: Connection[]): Record<string, Connection[]> {
  const out: Record<string, Connection[]> = {};
  for (const c of items) {
    out[c.definition_slug] = out[c.definition_slug] ?? [];
    out[c.definition_slug].push(c);
  }
  return out;
}
